import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, TextField, IconButton, CircularProgress, Divider } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { useTicketThread, usePostTicketMessage, useMarkTicketRead } from '../../hooks/useSupport';

const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const isImageFile = (name) => /\.(png|jpe?g|gif|webp)(?:$|[?#])/i.test(name || '');

const FileIcon = ({ name, sx }) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return <PictureAsPdfIcon sx={sx} />;
  if (ext === 'doc' || ext === 'docx') return <DescriptionIcon sx={sx} />;
  return <InsertDriveFileIcon sx={sx} />;
};

/** The attachment on a message — an inline image preview, or a filename card
 * (styled to match whichever side's bubble it sits under: accent-filled for
 * your own messages, white/bordered for theirs). */
const Attachment = ({ url, name, isOwn, accent, handoffMode = false }) => {
  if (isImageFile(name) || isImageFile(url)) {
    return (
      <Box component="a" href={url} target="_blank" rel="noreferrer" sx={{
        display: 'block', width: 180, height: 180, borderRadius: '12px', overflow: 'hidden',
        border: '1px solid #eef0f4', lineHeight: 0, bgcolor: '#f6f7fc',
      }}>
        {/* Plain `style` (not sx) — some global stylesheet elsewhere in the app
            was beating the emotion-generated width rule, so a tiny source image
            (e.g. an icon-sized screenshot) rendered at its native size instead
            of filling the card. Inline style always wins. */}
        <img
          src={url} alt={name}
          style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
        />
      </Box>
    );
  }
  return (
    <Box component="a" href={url} target="_blank" rel="noreferrer" sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      p: handoffMode ? '9px 10px' : undefined, px: handoffMode ? undefined : 1.25, py: handoffMode ? undefined : 1,
      borderRadius: '12px',
      bgcolor: isOwn && !handoffMode ? accent : '#fff',
      background: isOwn && handoffMode ? `linear-gradient(135deg, ${accent} 0%, #5c3fd6 100%)` : undefined,
      border: isOwn ? 'none' : '1px solid #eef0f4',
      color: isOwn ? '#fff' : '#111', textDecoration: 'none', maxWidth: 220,
    }}>
      <FileIcon name={name} sx={{ fontSize: handoffMode ? 19 : 20, flexShrink: 0 }} />
      <Typography sx={{ fontSize: handoffMode ? '11px' : '.78rem', fontWeight: handoffMode ? 700 : 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {name}
      </Typography>
    </Box>
  );
};

const LooseAiTranscript = ({ messages, accent, employeeName = 'Employee' }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1.25 }}>
    {messages.map((message, index) => {
      const fromEmployee = message.role === 'user';
      return (
        <Box
          key={`${message.role}-${index}`}
          sx={{ display: 'flex', justifyContent: fromEmployee ? 'flex-end' : 'flex-start' }}
        >
          <Box sx={{
            maxWidth: '86%', px: 1.5, py: 1.25,
            borderRadius: fromEmployee ? '16px 5px 16px 16px' : '5px 16px 16px 16px',
            color: fromEmployee ? '#fff' : '#222c58',
            background: fromEmployee ? `linear-gradient(135deg, ${accent} 0%, #5c3fd6 100%)` : '#f2f4f9',
          }}>
            <Typography sx={{ mb: .375, fontSize: '.66rem', lineHeight: 1.2, fontWeight: 800, opacity: .72 }}>
              {fromEmployee ? employeeName : 'Aira'}
            </Typography>
            <Typography component="div" sx={{ fontSize: '.875rem', lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {message.content}
            </Typography>
          </Box>
        </Box>
      );
    })}
  </Box>
);

const HandoffBanner = ({ ticket, onManageStatus }) => {
  if (!ticket) return null;
  const assignedName = ticket.assigned_hr_name || ticket.assigned_hr?.name || '';
  const branch = ticket.assigned_hr_branch || ticket.assigned_hr?.branch || '';
  const location = ticket.assigned_hr_location || ticket.assigned_hr?.location || '';
  const assignment = [assignedName, branch, location].filter(Boolean).join(' · ');
  const resolved = ticket.status === 'RESOLVED';
  const active = ['ANSWERED', 'AWAITING_EMPLOYEE'].includes(ticket.status)
    || (ticket.messages || []).some((message) => message.is_from_hr);
  const assigned = Boolean(assignedName);
  const title = resolved
    ? 'This conversation is resolved'
    : active
      ? `${assignedName || 'HR support'} joined the conversation`
      : assigned
        ? `${assignedName} is assigned`
        : 'Waiting for an HR agent';
  const detail = resolved
    ? 'The employee can start a new request if more help is needed.'
    : active
      ? 'Messages you send here go directly to the employee.'
      : assigned
        ? 'The conversation is ready for the assigned HR to continue.'
        : 'This request is waiting to be assigned.';

  return (
    <Box sx={{
      m: '10px 14px 0', p: '10px 11px', border: '1px solid #eef0f4', borderRadius: '11px',
      bgcolor: '#fff', boxShadow: '0 2px 8px rgba(16,19,40,.04)',
    }}>
      <Box sx={{ display: 'flex', gap: '9px', alignItems: 'flex-start' }}>
        <Box sx={{
          width: 10, height: 10, mt: '5px', flex: '0 0 auto', borderRadius: '50%',
          bgcolor: resolved ? '#7c86aa' : assigned || active ? '#2aaa6b' : '#d6922e',
          boxShadow: resolved ? 'none' : assigned || active
            ? '0 0 0 3px rgba(42,170,107,.14)'
            : '0 0 0 3px rgba(214,146,46,.14)',
        }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography sx={{ fontSize: '.78rem', lineHeight: 1.35, fontWeight: 800, color: '#171b2f' }}>
            {title}
          </Typography>
          <Typography sx={{ mt: .25, fontSize: '.69rem', lineHeight: 1.35, color: '#626b8a' }}>
            {detail}
          </Typography>
          <Typography component="span" sx={{
            alignSelf: 'flex-start', mt: .75, px: .875, py: .25, borderRadius: '999px',
            bgcolor: '#f1f2f6', color: '#656b85', fontSize: '.6rem', lineHeight: 1.4,
            fontWeight: 750, letterSpacing: '.01em',
          }}>
            {assignment ? `Assigned HR: ${assignment}` : 'HR assignment pending'}
          </Typography>
        </Box>
      </Box>
      {onManageStatus && !resolved && (
        <Box
          component="button"
          type="button"
          onClick={onManageStatus}
          sx={{
            ml: '19px', mt: '5px', p: '2px 0', border: 0, color: '#111f68', bgcolor: 'transparent',
            font: 'inherit', fontSize: '11px', fontWeight: 750, textDecoration: 'underline', cursor: 'pointer',
          }}
        >
          Manage ticket status
        </Box>
      )}
    </Box>
  );
};

const HandoffMessage = ({ message, viewerIsHr, accent }) => {
  const isOwn = message.is_from_hr === viewerIsHr;
  const senderName = message.author_name || (message.is_from_hr ? 'HR' : 'Employee');

  return (
    <Box
      data-msg-id={message.id}
      sx={{
        display: 'flex', flexDirection: 'column', gap: '5px', mb: '14px',
        alignItems: isOwn ? 'flex-end' : 'flex-start',
      }}
    >
      {!isOwn && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', color: '#656b85' }}>
          <Box sx={{
            width: 24, height: 24, flex: '0 0 auto', borderRadius: '50%', bgcolor: '#eef0f4', color: '#656b85',
            fontSize: '9px', fontWeight: 800, display: 'grid', placeItems: 'center',
          }}>
            {initials(senderName)}
          </Box>
          <Typography sx={{ fontSize: '10.5px', lineHeight: 1.2, fontWeight: 700 }}>
            {senderName}
          </Typography>
        </Box>
      )}
      <Box sx={{
        width: 'fit-content', maxWidth: '82%', display: 'flex', flexDirection: 'column', gap: '5px',
        alignItems: isOwn ? 'flex-end' : 'flex-start', pl: isOwn ? 0 : '31px',
      }}>
        {message.body && (
          <Box sx={{
            p: '9px 12px', borderRadius: '14px', fontSize: '12.5px', lineHeight: 1.45,
            color: isOwn ? '#fff' : '#111', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
            bgcolor: isOwn ? undefined : '#f6f7fc',
            background: isOwn ? `linear-gradient(135deg, ${accent} 0%, #5c3fd6 100%)` : undefined,
            border: isOwn ? 0 : '1px solid #eef0f4',
          }}>
            {message.body}
          </Box>
        )}
        {message.attachment_url && (
          <Attachment
            url={message.attachment_url}
            name={message.attachment_name}
            isOwn={isOwn}
            accent={accent}
            handoffMode
          />
        )}
        <Typography sx={{
          fontSize: '9.5px', lineHeight: 1.2, color: isOwn ? accent : '#9298b2',
          display: 'flex', alignItems: 'center', gap: .4,
        }}>
          {fmtTime(message.created_at)}
          {isOwn && <span>· {message.seen ? '✓✓ Seen' : '✓ Sent'}</span>}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Shared thread body (messages + composer) for both the employee "Ask HR"
 * drawer and the HR "Support Inbox" drawer — same data, same grouping/
 * unread-divider/read-receipt rules, only the header and accent differ.
 * `viewerIsHr` flips which side's messages render as "own" (right, accent).
 */
export default function SupportChatThread({
  ticketId, viewerIsHr, accent, renderHeader, composerPlaceholder = 'Message…',
  handoffMode = false, onManageStatus,
}) {
  const { data: ticket, isLoading } = useTicketThread(ticketId, { refetchInterval: handoffMode ? 3000 : 15000 });
  const postMessage = usePostTicketMessage(ticketId);
  const markRead = useMarkTicketRead(ticketId);
  const [reply, setReply] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [sendError, setSendError] = useState('');
  const fileInputRef = useRef(null);
  const composerInputRef = useRef(null);
  const scrollRef = useRef(null);
  const [dismissedJump, setDismissedJump] = useState(false);
  const markedTicketRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  const messages = useMemo(() => ticket?.messages || [], [ticket]);
  const aiTranscript = useMemo(() => (
    Array.isArray(ticket?.ai_transcript)
      ? ticket.ai_transcript
        .filter((message) => ['user', 'assistant'].includes(message?.role) && message?.content)
        .map((message) => ({ role: message.role, content: String(message.content) }))
      : []
  ), [ticket]);
  const firstNewId = useMemo(() => messages.find((m) => m.is_new)?.id, [messages]);
  const newCount = useMemo(() => messages.filter((m) => m.is_new).length, [messages]);
  // Derived, never stored — a stored `showJump` boolean previously went stale
  // once a 15s background poll re-fetched with newCount already back to 0
  // (nothing had advanced it), showing a "0 new messages" banner.
  const shouldShowJump = !handoffMode && newCount > 0 && !dismissedJump;

  const groups = useMemo(() => {
    const out = [];
    messages.forEach((m) => {
      const prev = out[out.length - 1];
      if (prev && prev.is_from_hr === m.is_from_hr) prev.items.push(m);
      else out.push({ is_from_hr: m.is_from_hr, items: [m] });
    });
    return out;
  }, [messages]);

  // Mark-read fires when the thread is first opened, AND whenever new
  // messages stream in via poll while it's still open (the user is visibly
  // looking at it either way) — but never from a poll that found nothing new,
  // so an idle open drawer doesn't keep re-marking the same state.
  useEffect(() => {
    if (!ticket) return;
    const isNewTicket = markedTicketRef.current !== ticket.id;
    const grew = messages.length > prevMessageCountRef.current;
    if (isNewTicket || grew) {
      markedTicketRef.current = ticket.id;
      markRead.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id, messages.length]);

  useEffect(() => {
    if (!ticket || !scrollRef.current) return;
    if (messages.length > prevMessageCountRef.current) setDismissedJump(false);
    prevMessageCountRef.current = messages.length;

    if (handoffMode) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight });
    } else if (newCount > 0 && !dismissedJump) {
      const el = scrollRef.current.querySelector(`[data-msg-id="${firstNewId}"]`);
      if (el) el.scrollIntoView({ block: 'center' });
    } else {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight });
    }
    // Only re-run when the thread identity or message count changes, not on
    // every poll — otherwise a background refetch would yank the scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id, messages.length, handoffMode]);

  useEffect(() => {
    if (!handoffMode || !ticket?.id || ticket.status === 'RESOLVED') return;
    requestAnimationFrame(() => composerInputRef.current?.focus());
  }, [handoffMode, ticket?.id, ticket?.status]);

  const jumpToLatest = () => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    setDismissedJump(true);
  };

  const dismissJump = (e) => {
    e.stopPropagation();
    setDismissedJump(true);
  };

  const send = async () => {
    const text = reply.trim();
    if ((!text && !file) || postMessage.isPending) return;
    const pickedFile = file;
    setReply('');
    setFile(null);
    setFileError('');
    setSendError('');
    try {
      await postMessage.mutateAsync({ message: text, file: pickedFile });
      requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }));
    } catch (error) {
      setReply((current) => current || text);
      setFile((current) => current || pickedFile);
      const detail = error?.response?.data?.error || error?.response?.data?.detail || error?.message;
      setSendError(typeof detail === 'string' ? detail : 'Could not send your message. Please try again.');
    }
  };

  const pickFile = (e) => {
    const picked = e.target.files?.[0];
    if (picked?.size > MAX_ATTACHMENT_BYTES) {
      setFileError('Attachments must be 10 MB or smaller.');
      e.target.value = '';
      return;
    }
    if (picked) {
      setFileError('');
      setSendError('');
      setFile(picked);
    }
    e.target.value = ''; // allow re-picking the same file
  };

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%',
      ...(handoffMode && {
        color: '#111f68', fontFamily: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontSize: '14px', lineHeight: 1.45,
        '& .MuiTypography-root, & .MuiInputBase-root, & .MuiButtonBase-root': { fontFamily: 'inherit' },
      }),
    }}>
      {renderHeader ? renderHeader(ticket) : null}
      {handoffMode && <HandoffBanner ticket={ticket} onManageStatus={onManageStatus} />}

      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        {shouldShowJump && (
          <Box
            onClick={jumpToLatest}
            sx={{
              position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 2,
              display: 'flex', alignItems: 'center', gap: .5, bgcolor: '#fff', border: '1px solid #eef0f4',
              borderRadius: '999px', boxShadow: '0 4px 14px rgba(16,19,40,.14)', px: 1.25, py: .5, cursor: 'pointer',
            }}
          >
            <KeyboardArrowDownIcon fontSize="small" sx={{ color: accent }} />
            <Typography sx={{ fontSize: '.76rem', fontWeight: 600 }}>
              {newCount} new message{newCount === 1 ? '' : 's'}
            </Typography>
            <IconButton size="small" onClick={dismissJump} sx={{ p: .25 }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}

        <Box ref={scrollRef} sx={{ height: '100%', overflowY: 'auto', p: 2, bgcolor: '#fff' }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
          )}

          {handoffMode && aiTranscript.length > 0 && (
            <LooseAiTranscript messages={aiTranscript} accent={accent} employeeName={ticket?.employee_name || 'Employee'} />
          )}

          {!handoffMode && aiTranscript.length > 0 && (
            <Box
              component="details"
              open
              sx={{
                mb: 2, border: '1px solid #dfe5f2', borderRadius: '12px', bgcolor: '#f8faff',
                '& > summary': { cursor: 'pointer', listStylePosition: 'inside' },
              }}
            >
              <Box component="summary" sx={{ px: 1.5, py: 1.25, fontSize: '.78rem', fontWeight: 800, color: '#45506f' }}>
                AI conversation before handoff ({aiTranscript.length} messages)
              </Box>
              <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {aiTranscript.map((message, index) => {
                  const fromEmployee = message.role === 'user';
                  return (
                    <Box
                      key={`${message.role}-${index}`}
                      sx={{ display: 'flex', flexDirection: 'column', alignItems: fromEmployee ? 'flex-end' : 'flex-start' }}
                    >
                      <Typography sx={{ mb: .25, fontSize: '.65rem', fontWeight: 700, color: '#78819c' }}>
                        {fromEmployee ? 'Employee' : 'Aira'}
                      </Typography>
                      <Box sx={{
                        maxWidth: '88%', px: 1.25, py: .8, borderRadius: '10px', fontSize: '.78rem',
                        bgcolor: fromEmployee ? '#e8efff' : '#fff', color: '#25304f', border: '1px solid #e1e6f0',
                      }}>
                        {message.content}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {handoffMode
            ? messages.map((message) => (
              <HandoffMessage key={message.id} message={message} viewerIsHr={viewerIsHr} accent={accent} />
            ))
            : groups.map((g, gi) => {
            const isOwn = g.is_from_hr === viewerIsHr;
            const last = g.items[g.items.length - 1];
            return (
              <Box key={gi} sx={{ mb: handoffMode ? '14px' : 1.5 }}>
                {!isOwn && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: .5 }}>
                    <Box sx={{
                      width: 24, height: 24, borderRadius: '50%', bgcolor: '#eef0f4', color: '#656b85',
                      fontSize: '.62rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {initials(g.items[0].author_name)}
                    </Box>
                    <Typography sx={{ fontSize: handoffMode ? '.66rem' : '.74rem', fontWeight: 700, color: '#656b85' }}>
                      {g.items[0].author_name || (g.is_from_hr ? 'HR' : 'Employee')}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', gap: .5, pl: isOwn ? 0 : 4 }}>
                  {g.items.map((m) => (
                    <React.Fragment key={m.id}>
                      {m.id === firstNewId && (
                        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1, my: .5 }}>
                          <Divider sx={{ flex: 1, borderColor: accent, opacity: .4 }} />
                          <Typography sx={{ fontSize: '.66rem', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                            New
                          </Typography>
                          <Divider sx={{ flex: 1, borderColor: accent, opacity: .4 }} />
                        </Box>
                      )}
                      <Box data-msg-id={m.id} sx={{ display: 'flex', flexDirection: 'column', gap: .5, alignItems: isOwn ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                        {m.body && (
                          <Box sx={{
                            px: 1.5, py: handoffMode ? '9px' : 1, borderRadius: '14px',
                            fontSize: handoffMode ? '.78rem' : '.85rem', lineHeight: handoffMode ? 1.45 : 'inherit',
                            bgcolor: isOwn && !handoffMode ? accent : isOwn ? undefined : '#f6f7fc',
                            background: isOwn && handoffMode ? `linear-gradient(135deg, ${accent} 0%, #5c3fd6 100%)` : undefined,
                            color: isOwn ? '#fff' : '#111',
                            border: isOwn ? 'none' : '1px solid #eef0f4',
                          }}>
                            {m.body}
                          </Box>
                        )}
                        {m.attachment_url && (
                          <Attachment url={m.attachment_url} name={m.attachment_name} isOwn={isOwn} accent={accent} />
                        )}
                      </Box>
                    </React.Fragment>
                  ))}
                  <Typography sx={{ fontSize: handoffMode ? '.59rem' : '.64rem', color: isOwn ? accent : '#9298b2', display: 'flex', alignItems: 'center', gap: .4 }}>
                    {fmtTime(last.created_at)}
                    {isOwn && <span>{last.seen ? '✓✓ Seen' : '✓ Sent'}</span>}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {(!handoffMode || ticket?.status !== 'RESOLVED') && (
        <>
          <Divider />
          <Box sx={{ p: handoffMode ? '11px 12px' : 1.5, minHeight: handoffMode ? 64 : undefined, bgcolor: '#fff' }}>
        {file && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: .75, mb: 1, px: 1.25, py: .75,
            bgcolor: '#f6f7fc', borderRadius: '10px',
          }}>
            <FileIcon name={file.name} sx={{ fontSize: 18, color: '#656b85', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '.78rem', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {file.name}
            </Typography>
            <IconButton size="small" onClick={() => { setFile(null); setFileError(''); setSendError(''); }} sx={{ p: .25 }}>
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        )}
        {(fileError || sendError) && (
          <Typography role="alert" sx={{ mb: .75, color: '#b6253c', fontSize: '11px' }}>
            {fileError || sendError}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: handoffMode ? '7px' : 1, alignItems: 'center' }}>
          <input ref={fileInputRef} type="file" hidden onChange={pickFile} />
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            aria-label="Attach a file"
            sx={{ color: '#656b85', width: handoffMode ? 38 : undefined, height: handoffMode ? 38 : undefined }}
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
          <TextField
            inputRef={composerInputRef}
            fullWidth size="small" placeholder={composerPlaceholder} value={reply}
            multiline={handoffMode}
            minRows={handoffMode ? 1 : undefined}
            maxRows={handoffMode ? 4 : undefined}
            onChange={(e) => { setReply(e.target.value); setSendError(''); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (!handoffMode || !e.shiftKey) && !postMessage.isPending) {
                e.preventDefault();
                send();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: handoffMode ? 40 : undefined, maxHeight: handoffMode ? 92 : undefined,
                borderRadius: '999px', bgcolor: '#f6f7fc', pl: handoffMode ? 0 : .5,
                fontSize: handoffMode ? '.875rem' : undefined,
              },
              '& .MuiInputBase-inputMultiline': handoffMode ? {
                maxHeight: 72, overflowY: 'auto', p: '10px 14px !important', lineHeight: 1.4,
              } : undefined,
              '& fieldset': { border: 'none' },
            }}
          />
          <IconButton
            onClick={send} disabled={(!reply.trim() && !file) || postMessage.isPending}
            sx={{
              bgcolor: handoffMode ? undefined : accent,
              background: handoffMode ? `linear-gradient(135deg, ${accent} 0%, #5c3fd6 100%)` : undefined,
              color: '#fff', width: handoffMode ? 38 : undefined, height: handoffMode ? 38 : undefined,
              '&:hover': { filter: 'brightness(1.08)' },
              '&.Mui-disabled': { bgcolor: '#eef0f4', background: '#eef0f4' },
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
